/**
 * Runtime view-model schemas — the shared leaves of the BFF contract.
 *
 * Split out of `bff-schemas.ts` (which sits under a 500-code-line ceiling):
 * these are the runtime twins of `shared/lib/types/*` and the package VMs, plus
 * the branded/enum leaf helpers they are built from. They are consumed by the
 * request-body schemas and the per-endpoint maps, which stay in `bff-schemas.ts`
 * next to the parity gate that proves them identical to the contract.
 *
 * Everything here is exported only so that sibling module can import it — this
 * is one module split in two for size, not a new public surface. `bff-schemas.ts`
 * remains the door the seam uses.
 */
import { OTP_CODE_LENGTH } from '@letsgethome/lib/constants';
import { PROTO_FIELD_BOUNDS } from '@letsgethome/lib/proto-bounds';
import { PROTO_ENUM_VALUE_SETS } from '@letsgethome/lib/proto-enums/value_sets_gen';
import type { ServerErrorReason } from '@letsgethome/lib/server-errors';
import { WizardStep } from '@letsgethome/proto/account/v1/listings_pb';
import { PermissionSlug } from '@letsgethome/proto/auth/v1/permission_slug_pb';
import { RoleName } from '@letsgethome/proto/auth/v1/role_name_pb';
import { FloorLevel } from '@letsgethome/proto/common/v1/floor_level_pb';
import { ListingStatus } from '@letsgethome/proto/common/v1/listing_status_pb';
import { Locale } from '@letsgethome/proto/common/v1/locale_pb';
import {
  ListingSystemEvent,
  MessageKind,
  TopicType,
} from '@letsgethome/proto/conversations/v1/direct_pb';
import { SupportSystemEvent } from '@letsgethome/proto/conversations/v1/support_pb';
import {
  E164_WIRE_PATTERN,
  LOCALES,
  type BuildingId,
  type Cents,
  type ConversationId,
  type DistrictId,
  type E164Phone,
  type EstateId,
  type ImageId,
  type ListingId,
  type MessageId,
  type ParticipantId,
  type PublicId,
  type S3Key,
  type Slug,
  type UserId,
} from '@letsgethome/types/branded';
import * as z from 'zod/mini';

import {
  brandedNumber,
  brandedString,
  protoEnumNameSchema,
  protoEnumValueSchema,
} from './bff-schema-helpers';

// ---------------------------------------------------------------------------
// Leaf helpers — branded/enum wire values (see bff-schema-helpers.ts)

// ---------------------------------------------------------------------------

export const localeSchema = z.enum(LOCALES);
export const transactionModeSchema = z.enum(PROTO_ENUM_VALUE_SETS.TransactionMode);
export const suggestionTypeSchema = z.enum(PROTO_ENUM_VALUE_SETS.SuggestionType);
export const intentTypeSchema = z.enum(PROTO_ENUM_VALUE_SETS.IntentType);

/**
 * Branded `E164Phone` with its runtime wire-shape twin (W3.4) — the former
 * bare `z.string()` accepted ANY string at the OTP seam; now the shape
 * check mirrors the proto `E164PhoneNumber` CEL via the shared pattern.
 */
export const e164PhoneSchema = z.custom<E164Phone>(
  (value) => typeof value === 'string' && E164_WIRE_PATTERN.test(value)
);

/**
 * OTP code wire shape, derived from the `domain_core::input` SSOT length
 * (the same constant the input field caps at) — not a hand-typed 6.
 */
export const OTP_CODE_WIRE_PATTERN = new RegExp(`^[0-9]{${OTP_CODE_LENGTH}}$`);
export const otpCodeSchema = z.custom<string>(
  (value) => typeof value === 'string' && OTP_CODE_WIRE_PATTERN.test(value)
);

/**
 * Max listing-description length — the GENERATED mirror of the enforcing
 * SSOT, the proto `(buf.validate.field).string.max_len` on
 * `account.v1.UpdateListingStepRequest.description` (protovalidate rejects
 * an over-long body server-side regardless). Bounding the zod twin here
 * gives the wizard early feedback instead of accepting ~20k chars and
 * bouncing off the server (R6/R7, auth-debt round 2). The value comes from
 * `@letsgethome/lib/proto-bounds` (protoboundsgen, audit Train H) — derived
 * from the compiled descriptor set, so it CANNOT drift from the wire.
 */
export const LISTING_DESCRIPTION_MAX_LENGTH =
  PROTO_FIELD_BOUNDS['account.v1.UpdateListingStepRequest.description'].maxLen;
export const listingDescriptionSchema = z
  .string()
  .check(z.maxLength(LISTING_DESCRIPTION_MAX_LENGTH));

/**
 * Max profile-name length — the GENERATED mirror of the proto
 * `(buf.validate.field).string.max_len` on
 * `account.v1.UpdateProfileRequest.{given_name,family_name}` (protovalidate
 * enforces server-side regardless), same generated source as the
 * listing-description mirror above.
 */
export const PROFILE_NAME_MAX_LENGTH =
  PROTO_FIELD_BOUNDS['account.v1.UpdateProfileRequest.given_name'].maxLen;
export const profileNameSchema = z.string().check(z.maxLength(PROFILE_NAME_MAX_LENGTH));

/**
 * Start-inquiry submission bounds — GENERATED mirrors of the proto
 * `buf.validate` rules on `account.v1.StartInquiryRequest` (same
 * protoboundsgen source as the listing-description mirror above), so the
 * early web feedback CANNOT drift from what protovalidate enforces on the
 * wire. `message` is additionally `minLength(1)`: this endpoint is the
 * SUBMIT mode of the two-mode `StartInquiry` RPC (message present ⇒ Lead +
 * Conversation + Message created); the message-less form-load mode belongs
 * to the SSR loader, never this route.
 */
export const INQUIRY_MESSAGE_MAX_LENGTH =
  PROTO_FIELD_BOUNDS['account.v1.StartInquiryRequest.message'].maxLen;
export const inquiryMessageSchema = z
  .string()
  .check(z.minLength(1), z.maxLength(INQUIRY_MESSAGE_MAX_LENGTH));
/** Support first-message ceiling (messaging PR-H1; wire `max_len: 10000`). */
export const SUPPORT_MESSAGE_MAX_LENGTH =
  PROTO_FIELD_BOUNDS['conversations.v1.EnsureSupportCaseRequest.initial_message'].maxLen;
/** Occupants ceiling (wire `lte: 100`; the `gt: 0` floor is mirrored by hand). */
export const INQUIRY_OCCUPANTS_MAX =
  PROTO_FIELD_BOUNDS['account.v1.StartInquiryRequest.occupants_count'].lte;
export const inquiryOccupantsSchema = z.int().check(z.gte(1), z.lte(INQUIRY_OCCUPANTS_MAX));
/** Sale-horizon ceiling (wire `lte: 600` months). */
export const INQUIRY_SALE_TIMEFRAME_MAX =
  PROTO_FIELD_BOUNDS['account.v1.StartInquiryRequest.sale_timeframe_months'].lte;
export const inquirySaleTimeframeSchema = z
  .int()
  .check(z.gte(0), z.lte(INQUIRY_SALE_TIMEFRAME_MAX));
/**
 * Lease-term ceiling (F-1; wire `lte: 600` months). The `gt: 0` floor —
 * DELIBERATELY unlike the sale horizon's `gte: 0` — is mirrored by hand as
 * `gte(1)`: a zero-month lease is not a real term.
 */
export const INQUIRY_LEASE_TERM_MAX =
  PROTO_FIELD_BOUNDS['account.v1.StartInquiryRequest.lease_term_months'].lte;
export const inquiryLeaseTermSchema = z.int().check(z.gte(1), z.lte(INQUIRY_LEASE_TERM_MAX));
/** The `<input type="date">` value shape the server converts to `common.v1.Date`. */
export const isoDateStringSchema = z.string().check(z.regex(/^\d{4}-\d{2}-\d{2}$/));

// ---------------------------------------------------------------------------
// Shared view-model schemas (runtime twins of `shared/lib/types/*` + packages)
// ---------------------------------------------------------------------------

/** `@letsgethome/lib/i18n` `LocalizedStringData`. */
export const localizedStringDataSchema = z.object({
  en: z.string(),
  zhHk: z.string(),
  zhCn: z.string(),
});

/** `shared/lib/types/geo` `LatLngData` (wire epoch 19 — absence = no position). */
export const latLngDataSchema = z.object({
  lat: z.number(),
  lng: z.number(),
});

/** `@letsgethome/lib/utils/timestamp` `TimestampData` (readonly VM). */
export const timestampDataSchema = z.readonly(
  z.object({
    iso: z.string(),
    epochMs: z.number(),
  })
);

/** `@letsgethome/lib/auth/types` `UserProfile`. */
export const userProfileSchema = z.object({
  id: brandedString<UserId>(),
  givenName: z.string(),
  familyName: z.string(),
  avatarKey: z.optional(brandedString<S3Key>()),
  email: z.optional(z.string()),
  isStaff: z.optional(z.boolean()),
  roles: z.optional(z.array(protoEnumValueSchema<RoleName>(RoleName))),
  permissions: z.optional(z.array(protoEnumValueSchema<PermissionSlug>(PermissionSlug))),
});

/**
 * `@letsgethome/lib/server-errors` `ServerErrorInfo` — the error half of every
 * `BffResult` envelope. `reason` is validated as a string, not against the
 * generated reason vocabulary: an unknown reason must PASS (forward-compat — a
 * newer backend reason on an older client degrades to the generic message, see
 * `decodeServerError`, which performs this same sanctioned crossing).
 */
export const serverErrorInfoSchema = z.object({
  message: z.string(),
  reason: z.optional(z.string() as z.ZodMiniType<ServerErrorReason>),
  metadata: z.optional(z.record(z.string(), z.string())),
});

/** `shared/lib/types/conversations` `MessageData`. */
export const messageDataSchema = z.object({
  id: brandedString<MessageId>(),
  conversationId: brandedString<ConversationId>(),
  // r6 W-4: the runtime twin of the `ParticipantId | null` / `S3Key | null`
  // seam — a SYSTEM-authored row has no participant and a sender may have no
  // avatar, so the wire's `''` is projected to `null` by the mapper rather
  // than branded.
  participantId: z.nullable(brandedString<ParticipantId>()),
  senderName: z.string(),
  senderAvatarKey: z.nullable(brandedString<S3Key>()),
  isCurrentUser: z.boolean(),
  content: z.string(),
  kind: protoEnumNameSchema(MessageKind),
  systemEvent: protoEnumNameSchema(ListingSystemEvent),
  supportSystemEvent: protoEnumNameSchema(SupportSystemEvent),
  createdAt: timestampDataSchema,
  // Messaging PR-J-c: the viewer's stored translation (absent = render
  // `content` untouched) + the sender's authored locale as its NAME.
  translatedContent: z.optional(z.string()),
  sourceLocale: protoEnumNameSchema(Locale),
});

/** `shared/lib/types/conversations` `ParticipantData`. */
export const participantDataSchema = z.object({
  id: brandedString<ParticipantId>(),
  userId: brandedString<UserId>(),
  name: z.string(),
  // r6 W-4: runtime twin of `S3Key | null` — a participant may have no avatar,
  // and the mapper projects the wire's `''` to `null`, never a branded empty.
  avatarKey: z.nullable(brandedString<S3Key>()),
  isCurrentUser: z.boolean(),
});

/** `shared/lib/types/conversations` `TopicData`. */
export const topicDataSchema = z.object({
  title: z.string(),
  imageKey: brandedString<S3Key>(),
  subtitle: z.string(),
});

/**
 * `shared/lib/types/conversations` `ConversationData` (W-6).
 *
 * `topic` / `lastMessage` / `lastMessageAt` are NULLABLE, not optional: the
 * mapper writes an explicit `null` for a conversation with no topic or no
 * messages yet, and the view-model type says so — `z.nullable` (not
 * `z.optional`) is what the exactness gate below demands.
 */
export const conversationDataSchema = z.object({
  id: brandedString<ConversationId>(),
  topicType: protoEnumNameSchema(TopicType),
  topicId: z.string(),
  topic: z.nullable(topicDataSchema),
  lastMessage: z.nullable(messageDataSchema),
  lastMessageAt: z.nullable(timestampDataSchema),
  unreadCount: z.number(),
  participants: z.array(participantDataSchema),
});

/** `@letsgethome/realtime` `RealtimeTokens`. */
export const realtimeTokensSchema = z.object({
  connectionToken: z.string(),
  subscriptionToken: z.string(),
  websocketUrl: z.string(),
});

/** `shared/lib/types/estates` `NearbyPoiData` (SEO F2). */
export const nearbyPoiDataSchema = z.object({
  name: localizedStringDataSchema,
  distanceM: z.number(),
});

/** `shared/lib/types/estates` `BuildingData`. */
export const buildingDataSchema = z.object({
  id: brandedString<BuildingId>(),
  name: localizedStringDataSchema,
  fullName: localizedStringDataSchema,
  activeTotalCount: z.number(),
  activeRentCount: z.number(),
  activeSaleCount: z.number(),
  nearMtr: z.boolean(),
  closestMtrStation: z.optional(nearbyPoiDataSchema),
  closestSchool: z.optional(nearbyPoiDataSchema),
  closestHospital: z.optional(nearbyPoiDataSchema),
  closestCarPark: z.optional(nearbyPoiDataSchema),
  coordinate: z.optional(latLngDataSchema),
});

/** `shared/lib/types/listing-draft` `ListingImageData`. */
export const listingImageDataSchema = z.object({
  id: brandedString<ImageId>(),
  key: brandedString<S3Key>(),
  sortOrder: z.number(),
  filename: z.optional(z.string()),
});

/** `shared/lib/types/listing-draft` `ListingDraftData`. */
export const listingDraftDataSchema = z.object({
  id: brandedString<ListingId>(),
  publicId: z.optional(brandedString<PublicId>()),
  slug: z.optional(brandedString<Slug>()),
  status: protoEnumNameSchema(ListingStatus),
  nextStep: z.optional(protoEnumNameSchema(WizardStep)),
  wizardComplete: z.boolean(),
  // ADR 0151 — the backend's evaluation of listing.payment_gateway for
  // the lister; the wizard step list is a pure function of it.
  paymentRequired: z.boolean(),
  buildingId: z.optional(brandedString<BuildingId>()),
  manualAddress: z.optional(z.string()),
  floorLevel: z.optional(protoEnumNameSchema(FloorLevel)),
  floor: z.optional(z.string()),
  unit: z.optional(z.string()),
  saleableArea: z.optional(z.number()),
  forSale: z.boolean(),
  forRent: z.boolean(),
  salePriceCents: z.optional(brandedNumber<Cents>()),
  rentPriceCents: z.optional(brandedNumber<Cents>()),
  description: z.optional(listingDescriptionSchema),
  images: z.array(listingImageDataSchema),
  buildingName: z.optional(z.string()),
  estateName: z.optional(z.string()),
  districtName: z.optional(z.string()),
  estateId: z.optional(brandedString<EstateId>()),
  estateLat: z.optional(z.number()),
  estateLng: z.optional(z.number()),
});

/** `shared/lib/types/listing-card` `ListingCardData` (`Linkable` fields inline). */
export const listingCardDataSchema = z.object({
  publicId: brandedString<PublicId>(),
  slug: brandedString<Slug>(),
  id: brandedString<ListingId>(),
  buildingId: brandedString<BuildingId>(),
  rentPriceCents: brandedNumber<Cents>(),
  salePriceCents: brandedNumber<Cents>(),
  forRent: z.boolean(),
  forSale: z.boolean(),
  bedrooms: z.number(),
  bathrooms: z.number(),
  saleableArea: z.number(),
  imageKeys: z.array(brandedString<S3Key>()),
  saved: z.boolean(),
  badge: z.string(),
  status: z.optional(protoEnumNameSchema(ListingStatus)),
  coordinate: z.optional(latLngDataSchema),
  buildingName: z.string(),
  estateName: z.string(),
});

/** `shared/lib/types/search` `SearchListingsResult` (+ `MapPositionData`). */
export const searchListingsResultSchema = z.object({
  listings: z.array(listingCardDataSchema),
  totalCount: z.number(),
  page: z.number(),
  perPage: z.number(),
  hasMore: z.boolean(),
  mapPosition: z.optional(
    z.object({
      centerLat: z.number(),
      centerLng: z.number(),
      zoom: z.number(),
    })
  ),
});

/** The fields every `SuggestionItemData` variant shares (see the union below). */
export const suggestionItemCommonShape = {
  publicId: brandedString<PublicId>(),
  title: localizedStringDataSchema,
  subtitle: localizedStringDataSchema,
  localizedSlug: localizedStringDataSchema,
  parentSlug: localizedStringDataSchema,
  count: z.number(),
};

/**
 * `shared/lib/types/suggestions` `SuggestionItemData` — discriminated on
 * `type`, one arm per `storefront.v1.SuggestionType` variant carrying its
 * BRANDED id (mirrors `SuggestionItemVariants`; the parity gate below proves
 * the runtime union identical to the VM union, so an arm can't drift).
 */
export const suggestionItemDataSchema = z.discriminatedUnion('type', [
  z.object({
    ...suggestionItemCommonShape,
    type: z.literal('district'),
    id: brandedString<DistrictId>(),
  }),
  z.object({
    ...suggestionItemCommonShape,
    type: z.literal('estate'),
    id: brandedString<EstateId>(),
  }),
  z.object({
    ...suggestionItemCommonShape,
    type: z.literal('building'),
    id: brandedString<BuildingId>(),
  }),
  z.object({
    ...suggestionItemCommonShape,
    type: z.literal('listing'),
    id: brandedString<ListingId>(),
  }),
]);

/** `shared/lib/types/suggestions` `SearchSuggestionsData`. */
export const searchSuggestionsDataSchema = z.object({
  sections: z.array(
    z.object({
      type: suggestionTypeSchema,
      items: z.array(suggestionItemDataSchema),
    })
  ),
});

/** `shared/lib/types/suggestions` `FiltersMetadataData`. */
export const filtersMetadataDataSchema = z.object({
  histogram: z.array(
    z.object({
      bucketNumber: z.number(),
      height: z.number(),
      minPriceCents: z.string(),
      maxPriceCents: z.string(),
    })
  ),
  resultsCount: z.number(),
});

/** `shared/lib/types/wizard` `EstateSuggestionData`. */
export const estateSuggestionDataSchema = z.object({
  id: brandedString<EstateId>(),
  publicId: brandedString<PublicId>(),
  name: z.optional(localizedStringDataSchema),
  districtName: z.optional(localizedStringDataSchema),
  coordinate: z.optional(latLngDataSchema),
});

/** `shared/lib/types/wizard` `BuildingSuggestionData`. */
export const buildingSuggestionDataSchema = z.object({
  id: brandedString<BuildingId>(),
  name: z.optional(localizedStringDataSchema),
  fullName: z.optional(localizedStringDataSchema),
});
